import { useState, useRef, useCallback, useEffect, type ChangeEvent } from 'react';
import { Upload, FileText, CircleAlert as AlertCircle, Check, X, ArrowRight, ArrowLeft, MapPin, Users, Loader as Loader2, TriangleAlert as AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';
import { Sentry } from '../../lib/sentry';
import Modal from '../common/Modal';

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

const FIELD_KEYWORDS: Record<string, string[]> = {
  company_name: ['company_name', 'company', 'name', 'organisation', 'organization', 'corp', 'account_name', 'account'],
  website_url: ['website_url', 'website', 'url', 'domain', 'web', 'site', 'homepage'],
  ticker_symbol: ['ticker_symbol', 'ticker', 'stock_symbol', 'symbol', 'stock'],
};

function autoMapColumn(header: string): string | null {
  const h = header.toLowerCase().replace(/[^a-z0-9_]/g, '_');
  for (const [field, keywords] of Object.entries(FIELD_KEYWORDS)) {
    if (keywords.some(k => h === k || h.includes(k))) return field;
  }
  return null;
}

interface ParsedRow {
  rowNumber: number;
  company_name: string;
  website_url: string;
  ticker_symbol: string;
  status: 'new' | 'duplicate_own' | 'duplicate_other' | 'missing_fields' | 'csv_duplicate';
  ownerName?: string;
  ownerUserId?: string;
  existingAccountId?: string;
  warning?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onUploadStarted: (uploadId: string) => void;
}

interface BulkUploadCooldownResponse {
  allowed: boolean;
  next_allowed_at: string | null;
}

interface DuplicateCheckResult {
  row_number: number;
  status: 'new' | 'duplicate_own' | 'duplicate_other';
  warning: string | null;
  existing_account_id: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
}

export default function BulkAccountUpload({ isOpen, onClose, onUploadStarted }: Props) {
  const { organisation, user } = useAuth();
  const { apiGet, apiPost } = useApi();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<'file' | 'map' | 'review'>('file');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Cooldown state
  const [cooldownUntil, setCooldownUntil] = useState<Date | null>(null);
  const [cooldownChecked, setCooldownChecked] = useState(false);

  // File state
  const [fileName, setFileName] = useState('');
  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<string[][]>([]);

  // Mapping state
  const [columnMap, setColumnMap] = useState<Record<string, number>>({
    company_name: -1,
    website_url: -1,
    ticker_symbol: -1,
  });

  // Validation state
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [validating, setValidating] = useState(false);

  // Check cooldown on open (per-org)
  useEffect(() => {
    if (!isOpen || !user || !organisation) return;
    setCooldownChecked(false);
    (async () => {
      try {
        const data = await apiGet<BulkUploadCooldownResponse>(
          `/v1/bulk-account-uploads/cooldown?org_id=${encodeURIComponent(organisation.id)}`,
        );
        if (!data.allowed && data.next_allowed_at) {
          setCooldownUntil(new Date(data.next_allowed_at));
        } else {
          setCooldownUntil(null);
        }
      } catch (err) {
        Sentry.captureException(err);
        setCooldownUntil(null);
        setError('Failed to check upload cooldown. Please try again.');
      } finally {
        setCooldownChecked(true);
      }
    })();
  }, [apiGet, isOpen, user, organisation]);

  const reset = () => {
    setStep('file');
    setError('');
    setFileName('');
    setRawHeaders([]);
    setRawRows([]);
    setColumnMap({ company_name: -1, website_url: -1, ticker_symbol: -1 });
    setParsedRows([]);
    setValidating(false);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');

    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        setError('CSV must contain a header row and at least one data row.');
        return;
      }
      if (lines.length > 101) {
        setError(`CSV contains ${lines.length - 1} rows. Maximum is 100 accounts per upload.`);
        return;
      }

      const headers = parseCsvLine(lines[0]);
      const rows = lines.slice(1).map(l => parseCsvLine(l)).filter(r => r.some(c => c));

      if (rows.length === 0) {
        setError('No data rows found in the CSV.');
        return;
      }
      if (rows.length > 100) {
        setError(`CSV contains ${rows.length} data rows. Maximum is 100.`);
        return;
      }

      setFileName(file.name);
      setRawHeaders(headers);
      setRawRows(rows);

      // Auto-map columns
      const autoMap: Record<string, number> = { company_name: -1, website_url: -1, ticker_symbol: -1 };
      headers.forEach((h, i) => {
        const field = autoMapColumn(h);
        if (field && autoMap[field] === -1) autoMap[field] = i;
      });
      setColumnMap(autoMap);
      setStep('map');
    };
    reader.readAsText(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file || !file.name.endsWith('.csv')) {
      setError('Please drop a .csv file');
      return;
    }
    const input = fileRef.current;
    if (input) {
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, []);

  const canProceedToReview = columnMap.company_name >= 0 && columnMap.website_url >= 0;

  const validateRows = async () => {
    if (!organisation || !user) return;
    setValidating(true);
    setError('');

    try {
      const rows: ParsedRow[] = [];
      const seenNames = new Set<string>();

      for (let i = 0; i < rawRows.length; i++) {
        const cols = rawRows[i];
        const company = cols[columnMap.company_name]?.trim() || '';
        const website = cols[columnMap.website_url]?.trim() || '';
        const ticker = columnMap.ticker_symbol >= 0 ? (cols[columnMap.ticker_symbol]?.trim() || '') : '';

        if (!company || !website) {
          rows.push({
            rowNumber: i + 1,
            company_name: company,
            website_url: website,
            ticker_symbol: ticker,
            status: 'missing_fields',
            warning: !company ? 'Missing company name' : 'Missing website URL',
          });
          continue;
        }

        const nameKey = company.toLowerCase();
        if (seenNames.has(nameKey)) {
          rows.push({
            rowNumber: i + 1,
            company_name: company,
            website_url: website,
            ticker_symbol: ticker,
            status: 'csv_duplicate',
            warning: 'Duplicate within this CSV',
          });
          continue;
        }
        seenNames.add(nameKey);

        rows.push({
          rowNumber: i + 1,
          company_name: company,
          website_url: website,
          ticker_symbol: ticker,
          status: 'new',
        });
      }

      // Batch check for existing accounts in the org (server-side dedup + authz)
      const validRows = rows.filter(r => r.status === 'new');
      if (validRows.length > 0) {
        const { rows: results } = await apiPost<{ rows: DuplicateCheckResult[] }>(
          '/v1/bulk-account-uploads/validate-duplicates',
          {
            org_id: organisation.id,
            rows: validRows.map(r => ({
              row_number: r.rowNumber,
              company_name: r.company_name,
              website_url: r.website_url,
            })),
          },
        );

        const byRow = new Map(results.map(res => [res.row_number, res]));
        for (const row of validRows) {
          const res = byRow.get(row.rowNumber);
          if (!res || res.status === 'new') continue;
          row.status = res.status;
          if (res.existing_account_id) row.existingAccountId = res.existing_account_id;
          if (res.status === 'duplicate_other') {
            row.ownerUserId = res.owner_user_id ?? undefined;
            row.ownerName = res.owner_name || 'a colleague';
          } else if (res.warning) {
            row.warning = res.warning;
          }
        }
      }

      setParsedRows(rows);
      setStep('review');
    } catch (err) {
      Sentry.captureException(err);
      setError('Failed to validate accounts. Please try again.');
    } finally {
      setValidating(false);
    }
  };

  const newRows = parsedRows.filter(r => r.status === 'new');
  const duplicateOtherRows = parsedRows.filter(r => r.status === 'duplicate_other');
  const skippedRows = parsedRows.filter(r => ['duplicate_own', 'missing_fields', 'csv_duplicate'].includes(r.status));

  const startUpload = async () => {
    if (!organisation || !user || (newRows.length === 0 && duplicateOtherRows.length === 0)) return;
    setLoading(true);
    setError('');

    try {
      const teamRequestRows = duplicateOtherRows
        .filter(r => r.existingAccountId && r.ownerUserId)
        .map(r => ({
          row_number: r.rowNumber,
          company_name: r.company_name,
          website_url: r.website_url,
          ticker_symbol: r.ticker_symbol,
          account_id: r.existingAccountId!,
          owner_user_id: r.ownerUserId!,
          owner_name: r.ownerName || '',
        }));

      // Create the upload record and rows server-side (org-membership + cooldown enforced)
      const { upload_id, has_new_rows } = await apiPost<{ upload_id: string; has_new_rows: boolean }>(
        '/v1/bulk-account-uploads',
        {
          org_id: organisation.id,
          file_name: fileName,
          skipped_rows: skippedRows.length,
          new_rows: newRows.map(r => ({
            company_name: r.company_name,
            website_url: r.website_url,
            ticker_symbol: r.ticker_symbol,
          })),
          team_request_rows: teamRequestRows,
        },
      );

      // Send the actual join requests to account owners (duplicate requests are rejected with 409)
      for (const row of duplicateOtherRows) {
        if (!row.existingAccountId || !row.ownerUserId) continue;
        try {
          await apiPost(`/v1/account/${row.existingAccountId}/team-requests`, {
            org_id: organisation.id,
          });
        } catch (err) {
          if (!(err instanceof Error && err.message.includes('API 409'))) {
            throw err;
          }
        }
      }

      // Kick off the bulk enrichment when there are new accounts to process
      if (has_new_rows) {
        await apiPost('/v1/bulk-enrich-accounts', {
          upload_id,
          org_id: organisation.id,
          user_id: user.id,
        });
      }

      onUploadStarted(upload_id);
      handleClose();
    } catch (err) {
      Sentry.captureException(err);
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cooldownActive = cooldownUntil && cooldownUntil > new Date();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Bulk Upload Accounts" size="lg">
      {!cooldownChecked ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-5 h-5 text-app-muted animate-spin" />
        </div>
      ) : cooldownActive ? (
        <div className="space-y-4">
          <div className="p-5 bg-app-warning/10 border border-app-warning rounded-lg text-center">
            <AlertTriangle className="w-8 h-8 text-app-warning mx-auto mb-3" />
            <p className="text-sm font-semibold text-app-warning mb-1">Upload cooldown active</p>
            <p className="text-xs text-app-warning">
              You can upload another CSV after{' '}
              <span className="font-medium">
                {cooldownUntil!.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
              </span>
            </p>
            <p className="text-xs text-app-warning mt-2">There is a 24-hour cooldown between bulk uploads.</p>
          </div>
          <button onClick={handleClose} className="btn-ghost w-full">Close</button>
        </div>
      ) : step === 'file' ? (
        <div className="space-y-5">
          <p className="text-sm text-app-muted">
            Upload a CSV with up to 100 accounts. Each account will be enriched and tiered automatically.
          </p>
          <div
            className="border-2 border-dashed border-app-line rounded-lg p-8 text-center hover:border-forest-300 transition-colors cursor-pointer"
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
          >
            <Upload className="w-8 h-8 text-app-muted mx-auto mb-3" />
            <p className="text-sm font-medium text-app-ink mb-1">
              Drop CSV file here or click to browse
            </p>
            <p className="text-xs text-app-muted">Maximum 100 accounts per upload</p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFile}
            />
          </div>
          <div className="p-3 bg-app-canvas rounded-lg">
            <p className="text-xs font-medium text-app-muted mb-1.5">Required columns:</p>
            <div className="flex gap-4 text-xs text-app-muted">
              <span><span className="font-medium text-app-ink">Company name:</span> name of the company</span>
              <span><span className="font-medium text-app-ink">Website URL:</span> company website</span>
            </div>
            <p className="text-xs text-app-muted mt-1.5">Optional: Ticker Symbol</p>
          </div>
          {error && (
            <div className="flex items-center gap-2 p-3 bg-app-critical/10 border border-app-critical rounded-lg">
              <AlertCircle className="w-4 h-4 text-app-critical flex-shrink-0" />
              <p className="text-xs text-app-critical">{error}</p>
            </div>
          )}
        </div>
      ) : step === 'map' ? (
        <div className="space-y-5">
          <div className="flex items-center gap-2 text-sm text-app-muted">
            <FileText className="w-4 h-4" />
            <span className="font-medium">{fileName}</span>
            <span className="text-app-muted">{rawRows.length} rows</span>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-app-ink">Map your CSV columns</p>
            {(['company_name', 'website_url', 'ticker_symbol'] as const).map(field => {
              const required = field !== 'ticker_symbol';
              const label = field === 'company_name' ? 'Company Name' : field === 'website_url' ? 'Website URL' : 'Ticker Symbol';
              return (
                <div key={field} className="flex items-center gap-3">
                  <div className="w-36 text-sm text-app-ink">
                    {label} {required && <span className="text-app-critical">*</span>}
                  </div>
                  <MapPin className="w-3.5 h-3.5 text-app-muted" />
                  <select
                    value={columnMap[field]}
                    onChange={e => setColumnMap(prev => ({ ...prev, [field]: parseInt(e.target.value) }))}
                    className="flex-1 input-field !py-1.5 !text-sm"
                  >
                    <option value={-1}>{required ? 'Select column' : 'Not mapped'}</option>
                    {rawHeaders.map((h, i) => (
                      <option key={i} value={i}>{h}</option>
                    ))}
                  </select>
                  {columnMap[field] >= 0 && (
                    <Check className="w-4 h-4 text-app-positive" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Preview */}
          {canProceedToReview && (
            <div className="border border-app-line rounded-lg overflow-hidden">
              <p className="text-xs font-medium text-app-muted px-3 py-2 bg-app-canvas">Preview (first 3 rows)</p>
              <div className="divide-y divide-app-line">
                {rawRows.slice(0, 3).map((row, i) => (
                  <div key={i} className="px-3 py-2 text-xs text-app-muted flex gap-4">
                    <span className="font-medium text-app-ink truncate max-w-[180px]">
                      {row[columnMap.company_name] || '(empty)'}
                    </span>
                    <span className="text-app-muted truncate max-w-[200px]">
                      {row[columnMap.website_url] || '(empty)'}
                    </span>
                    {columnMap.ticker_symbol >= 0 && (
                      <span className="text-app-muted">{row[columnMap.ticker_symbol] || 'N/A'}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-app-critical/10 border border-app-critical rounded-lg">
              <AlertCircle className="w-4 h-4 text-app-critical flex-shrink-0" />
              <p className="text-xs text-app-critical">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={() => { setStep('file'); setError(''); }} className="btn-ghost flex-1">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </button>
            <button
              onClick={validateRows}
              disabled={!canProceedToReview || validating}
              className="btn-primary flex-1"
            >
              {validating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Validating...</>
              ) : (
                <><ArrowRight className="w-4 h-4 mr-2" /> Validate & Review</>
              )}
            </button>
          </div>
        </div>
      ) : step === 'review' ? (
        <div className="space-y-5">
          {/* Summary */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="p-3 bg-app-positive/10 border border-app-positive rounded-lg text-center">
              <p className="text-lg font-bold text-app-positive">{newRows.length}</p>
              <p className="text-xs text-app-positive">New accounts</p>
            </div>
            <div className="p-3 bg-app-focus/10 border border-app-focus rounded-lg text-center">
              <p className="text-lg font-bold text-app-focus">{duplicateOtherRows.length}</p>
              <p className="text-xs text-app-focus">Join requests</p>
            </div>
            <div className="p-3 bg-app-canvas border border-app-line rounded-lg text-center">
              <p className="text-lg font-bold text-app-muted">{skippedRows.length}</p>
              <p className="text-xs text-app-muted">Skipped</p>
            </div>
          </div>

          {/* Row details */}
          <div className="max-h-64 overflow-y-auto border border-app-line rounded-lg divide-y divide-app-line">
            {parsedRows.map(row => (
              <div key={row.rowNumber} className="px-3 py-2 flex items-center gap-3 text-xs">
                <span className="w-6 text-app-muted text-right">{row.rowNumber}</span>
                {row.status === 'new' && <Check className="w-3.5 h-3.5 text-app-positive flex-shrink-0" />}
                {row.status === 'duplicate_other' && <Users className="w-3.5 h-3.5 text-app-focus flex-shrink-0" />}
                {row.status === 'duplicate_own' && <AlertTriangle className="w-3.5 h-3.5 text-app-warning flex-shrink-0" />}
                {row.status === 'missing_fields' && <AlertCircle className="w-3.5 h-3.5 text-app-critical flex-shrink-0" />}
                {row.status === 'csv_duplicate' && <X className="w-3.5 h-3.5 text-app-muted flex-shrink-0" />}
                <span className="font-medium text-app-ink truncate max-w-[160px]">{row.company_name || '(empty)'}</span>
                <span className="text-app-muted truncate max-w-[140px]">{row.website_url || '(empty)'}</span>
                <span className="ml-auto flex-shrink-0">
                  {row.status === 'new' && <span className="text-app-positive">Will enrich</span>}
                  {row.status === 'duplicate_other' && (
                    <span className="text-app-focus">Owned by {row.ownerName}. Join request required.</span>
                  )}
                  {row.status === 'duplicate_own' && <span className="text-app-warning">{row.warning}</span>}
                  {row.status === 'missing_fields' && <span className="text-app-critical">{row.warning}</span>}
                  {row.status === 'csv_duplicate' && <span className="text-app-muted">Duplicate in CSV</span>}
                </span>
              </div>
            ))}
          </div>

          {newRows.length === 0 && duplicateOtherRows.length === 0 && (
            <div className="p-4 bg-app-warning/10 border border-app-warning rounded-lg text-center">
              <p className="text-sm text-app-warning">No new accounts to process. All rows are duplicates or have errors.</p>
            </div>
          )}

          {(newRows.length > 0 || duplicateOtherRows.length > 0) && (
            <div className="p-3 bg-app-focus/10 border border-app-focus rounded-lg">
              <p className="text-xs text-app-focus">
                {newRows.length > 0 && (
                  <><span className="font-medium">{newRows.length} accounts</span> will be enriched concurrently. This typically takes 60-120 seconds per account. </>
                )}
                {duplicateOtherRows.length > 0 && (
                  <><span className="font-medium">{duplicateOtherRows.length} join requests</span> will be sent to the account owners. </>
                )}
                Once enriched, all accounts will be auto-tiered.
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-app-critical/10 border border-app-critical rounded-lg">
              <AlertCircle className="w-4 h-4 text-app-critical flex-shrink-0" />
              <p className="text-xs text-app-critical">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={() => { setStep('map'); setError(''); }} className="btn-ghost flex-1">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </button>
            <button
              onClick={startUpload}
              disabled={loading || (newRows.length === 0 && duplicateOtherRows.length === 0)}
              className="btn-primary flex-1"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Upload & Process{newRows.length > 0 ? ` ${newRows.length} Accounts` : ''}</>
              )}
            </button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
